<template>
  <v-card class="pb-2" v-if="availableBill">
    <v-btn @click="$emit('refresh')" class="float-right ma-2" icon>
      <v-icon color="primary" dark>mdi-refresh-circle</v-icon>
    </v-btn>
    <v-card-title>
      <!-- / the href below is just to display the route 
      to the browser and allow right click - open in new tab to work-->
      <div>
        <a
          @click.stop="selectBill(trackedBill)"
          v-if="
            trackedBill &&
            trackedBill.ID &&
            !trackedBill.IsSummary &&
            !isExtended
          "
          :href="`#/bill/${trackedBill.ID}`"
          >{{ availableBill.bill }} - {{ availableBill.catchTitle }}</a
        >
        <div v-else-if="trackedBill && trackedBill.ID && !isExtended"
          ><a
            :href="`${baseBillLink}${availableBill.year}/${availableBill.bill}${
              !!availableBill.specialSessionValue
                ? '?specialSessionValue=' + availableBill.specialSessionValue
                : ''
            }`"
            target="_blank"
            >{{ availableBill.bill }} - {{ availableBill.catchTitle }}</a
          ></div
        >
        <div v-else-if="trackedBill && !trackedBill.IsActive" class="red--text"
          >{{ availableBill.bill }} - {{ availableBill.catchTitle }}
          <BaseTooltipChip
            label
            color="red"
            iconColor="white"
            small
            position="right"
            icon="mdi-clipboard-text-off"
            >Tracked Bill is not active</BaseTooltipChip
          >
        </div>
        <div v-else
          >{{ availableBill.bill }} - {{ availableBill.catchTitle }}</div
        >
      </div>
      <BaseHelpTooltip
        v-if="availableBill.currentBillHTML"
        x-small
        class="pa-1 d-flex"
        tooltipText="Bill Text"
        header="Current Bill Text"
        icon="icon-university"
        buttonClass="align-center"
      >
        <div class="viewer" v-html="availableBill.currentBillHTML"></div>
      </BaseHelpTooltip>
      <BaseHelpTooltip
        v-if="availableBill.fiscalNoteHTML"
        x-small
        class="pa-1 d-flex"
        tooltipText="Fiscal Note"
        header="Fiscal Note"
        icon="mdi-currency-usd"
        buttonClass="align-center"
      >
        <div class="viewer" v-html="availableBill.fiscalNoteHTML"></div>
      </BaseHelpTooltip>
      <BaseHelpTooltip
        v-if="availableBill.digestHTML"
        x-small
        class="pa-1 d-flex"
        tooltipText="Digest"
        header="Digest"
        icon="mdi-file-document-outline"
        buttonClass="align-center"
      >
        <div class="viewer" v-html="availableBill.digestHTML"></div>
      </BaseHelpTooltip>
      <BaseHelpTooltip
        v-if="availableBill.summaryHTML"
        x-small
        class="pa-1 d-flex"
        tooltipText="Summary"
        header="Summary"
        icon="mdi-format-align-left"
        buttonClass="align-center"
      >
        <div class="viewer" v-html="availableBill.summaryHTML"></div>
      </BaseHelpTooltip>
      <span
        class="pl-5"
        v-if="trackedBill && trackedBill.ID"
        v-for="a in owningAgencies"
      >
        <v-chip small label color="primary">{{ a.Abr }}</v-chip>
      </span>
      <span
        v-if="
          trackedBill &&
          trackedBill.ID &&
          trackedBill.TrackedBillDivisions &&
          trackedBill.TrackedBillDivisions.length > 0
        "
        ><span
          class="pl-2"
          v-for="d in trackedBill.TrackedBillDivisions.filter((v, i, list) => {
            return list.findIndex((l) => l.DivisionId == v.DivisionId) === i
          })"
        >
          <v-chip small outlined color="primary"
            ><span>
              <span v-if="owningAgencies.length > 1">
                {{ d.Division.Agency.Abr }}:
              </span>
              {{ d.Division.Description }}
            </span>
          </v-chip>
        </span>
      </span>
      <span v-if="trackedBill && trackedBill.ID > 0" class="pl-2">
        <BaseTooltipChip
          v-if="trackedBill.IsSummary"
          label
          color="secondary"
          small
          position="right"
          icon="mdi-eye-off"
          >Summary Only</BaseTooltipChip
        >
        <BaseTooltipChip
          v-if="this.trackedBill.currentUserPermissionID == 3"
          label
          color="secondary"
          small
          position="right"
          icon="mdi-eye"
          >Viewer</BaseTooltipChip
        >
        <BaseTooltipChip
          v-if="this.trackedBill.currentUserPermissionID == 2"
          label
          color="secondary"
          small
          position="right"
          icon="mdi-pencil"
          >Editor</BaseTooltipChip
        >
        <BaseTooltipChip
          v-if="this.trackedBill.currentUserPermissionID == 1"
          label
          position="right"
          color="secondary"
          small
          icon="mdi-check-decagram"
          >Owner</BaseTooltipChip
        >
        <BaseTooltipChip
          v-if="this.trackedBill.currentUserPermissionID == 5"
          label
          position="right"
          color="secondary"
          small
          icon="mdi-check-decagram"
          >Manager</BaseTooltipChip
        >
        <span class="ml-2">
          <BaseTooltipChip
            v-if="userIsAssigned"
            label
            position="right"
            color="green"
            iconColor="white"
            dark
            small
            icon="mdi-clipboard-text"
            >Assigned</BaseTooltipChip
          >
        </span>
      </span>
    </v-card-title>
    <v-card-subtitle v-if="bill.billTitle">{{
      bill.billTitle
    }}</v-card-subtitle>
    <v-card-subtitle v-else>
      <v-skeleton-loader v-bind="{}" type="text"></v-skeleton-loader>
    </v-card-subtitle>
    <v-card-text>
      <div
        v-if="availableBill.billStatus"
        class="text-h6"
        :class="`status-${availableBill.billStatus}`"
      >
        {{ availableBill.lastAction }} | Updated
        {{ dayjs(availableBill.lastActionDate).fromNow() }}
      </div>
      <div v-else
        ><v-skeleton-loader v-bind="{}" type="text"></v-skeleton-loader
      ></div>
      <div
        >Sponsor: {{ availableBill.sponsor }}
        <span v-if="availableBill.enrolledNumber">
          | Enrolled: {{ availableBill.enrolledNumber }}
          <span v-if="availableBill.chapter"> </span>
        </span>
        <span v-if="availableBill.chapter"
          >| Chapter: {{ availableBill.chapter }}
        </span></div
      >
      <div v-if="availableBill.id" class="pt-2"
        ><StatusGrid :bill="bill"></StatusGrid>
        <div
          class="mt-3"
          v-if="availableBill.amendments && availableBill.amendments.length > 0"
        >
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span>
                  <v-badge
                    color="primary"
                    :content="availableBill.amendments.length"
                  >
                    Amendments
                  </v-badge>
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th>Amendment</th>
                      <th>Origin</th>
                      <th>Sponsor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="amendment in availableBill.amendments">
                      <td
                        ><a :href="amendmentLink(amendment)" target="_blank">
                          {{ amendment.amendmentNumber }}
                        </a></td
                      >
                      <td>{{ amendment.order }}</td>
                      <td>{{ amendment.sponsor }}</td>
                      <td>{{ amendment.status }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
      <div class="pt-2" style="width: 50%" v-else>
        <v-skeleton-loader v-bind="{}" type="text@2"></v-skeleton-loader
      ></div>
      <slot name="tracker"></slot>
    </v-card-text>
    <v-card-actions v-if="bill.id">
      <v-row class="px-2">
        <v-btn
          v-if="trackedBill && !trackedBill.IsSummary"
          class="ma-1"
          target="_blank"
          :href="`${baseBillLink}${availableBill.year}/${availableBill.bill}${
            !!availableBill.specialSessionValue
              ? '?specialSessionValue=' + availableBill.specialSessionValue
              : ''
          }`"
          outlined
          color="primary"
        >
          <v-icon left> mdi-link </v-icon>WyoLeg</v-btn
        >
        <v-btn
          class="ma-1"
          v-if="bill.introduced"
          target="_blank"
          :href="`${basePdfLink}${bill.introduced}`"
          outlined
          color="primary"
        >
          <v-icon left> mdi-file-pdf-box </v-icon>Introduced</v-btn
        >
        <v-btn
          class="ma-1"
          v-if="bill.fiscalNote"
          target="_blank"
          :href="`${basePdfLink}${bill.fiscalNote}`"
          outlined
          color="primary"
          ><v-icon left> mdi-file-pdf-box </v-icon>Fiscal Note</v-btn
        >
        <v-btn
          class="ma-1"
          v-if="bill.digest"
          target="_blank"
          :href="`${basePdfLink}${bill.digest}`"
          outlined
          color="primary"
          ><v-icon left> mdi-file-pdf-box </v-icon>
          Digest
        </v-btn>
        <v-btn
          class="ma-1"
          v-if="bill.engrossedVersion"
          target="_blank"
          :href="`${basePdfLink}${bill.engrossedVersion}`"
          outlined
          color="primary"
          ><v-icon left> mdi-file-pdf-box </v-icon>
          Engross
        </v-btn>
        <v-btn
          class="ma-1"
          v-if="bill.enrolledAct"
          target="_blank"
          :href="`${basePdfLink}${bill.enrolledAct}`"
          outlined
          color="primary"
          ><v-icon left> mdi-file-pdf-box </v-icon>
          Enrolled
        </v-btn>
        <v-btn
          class="ma-1"
          v-if="bill.summary"
          target="_blank"
          :href="`${basePdfLink}${bill.summary}`"
          outlined
          color="primary"
          ><v-icon left> mdi-file-pdf-box </v-icon>
          Summary
        </v-btn>
        <!-- //make sure user has edit permissions for an agency -->
        <div class="ma-1">
          <v-btn
            v-if="
              userEditAgencies.length > 0 &&
              bill.id &&
              (!trackedBill || !trackedBill.ID)
            "
            @click="$refs.billTrackerForm.addTrackedBill(bill)"
            class="primary"
            ><v-icon left> mdi-plus </v-icon>
            Add Tracker
          </v-btn>
          <v-btn
            v-else-if="
              trackedBill &&
              trackedBill.ID &&
              !trackedBill.IsSummary &&
              userHasEditPerms
            "
            @click="$refs.billTrackerForm.editTrackedBill(trackedBill, bill)"
            class="primary"
            ><v-icon> mdi-pencil </v-icon>
            Edit Tracker
          </v-btn>
          <v-btn
            v-if="
              trackedBill &&
              trackedBill.ID &&
              !trackedBill.IsSummary &&
              (trackedBill.currentUserPermissionID == 1 ||
                trackedBill.currentUserPermissionID == 5) &&
              isExtended
            "
            @click="$refs.billPermissions.openPermissions(trackedBill)"
            class="primary ml-2"
            ><v-icon> mdi-account-lock </v-icon>
            Manage Permissions
          </v-btn>
          <v-btn
            v-if="
              trackedBill &&
              trackedBill.ID &&
              trackedBill.IsActive &&
              !trackedBill.IsSummary &&
              trackedBill.currentUserPermissionID == 1 &&
              isExtended
            "
            outlined
            color="red"
            @click="$refs.trackedBillDelete.deleteConfirm(trackedBill)"
            class="ml-2"
            ><v-icon> mdi-delete </v-icon>
            Delete Bill
          </v-btn>
          <BaseDeleteConfirm
            ref="trackedBillDelete"
            :delete="deleteTrackedBill"
            @refresh="loadTrackedBill"
          >
            Are you sure you want to delete this tracked bill?
          </BaseDeleteConfirm>
        </div>
      </v-row>
    </v-card-actions>
    <v-card-actions v-else
      ><v-skeleton-loader v-bind="{}" type="actions"></v-skeleton-loader
    ></v-card-actions>
    <TrackedBillDialogForm ref="billTrackerForm" @refresh="loadTrackedBill">
    </TrackedBillDialogForm>
    <BillPermissions
      v-if="
        trackedBill &&
        trackedBill.ID &&
        isExtended &&
        trackedBill.currentUserPermissionID
      "
      ref="billPermissions"
      @refreshBill="refreshPermissions"
      :trackedBillID="trackedBill.ID"
      :currentUserPermissionID="trackedBill.currentUserPermissionID"
    ></BillPermissions>
  </v-card>
</template>
<script>
import { get, sync, commit, call } from 'vuex-pathify'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)
import StatusGrid from '@components/lso/StatusGrid'
import TrackedBillDialogForm from '@components/form/add-edit/TrackedBillDialog'
import BillPermissions from '@components/form/grid/BillPermissions'
import { authComputed } from '@state/helpers'
export default {
  //model: {
  //	prop: ''
  //},
  props: {
    bill: {
      type: Object,
      default: {},
    },
    billStub: {
      type: Object,
      default: {},
    },
    trackedBill: {
      type: Object,
      default: null,
    },
    loading: { type: Boolean, default: true },
    isExtended: { type: Boolean, default: false },
  },
  components: { StatusGrid, TrackedBillDialogForm, BillPermissions },
  filters: {},
  data() {
    return {
      dayjs,
      basePdfLink: 'https://www.wyoleg.gov/',
      baseBillLink: 'https://www.wyoleg.gov/Legislation/',
    }
  },
  created() {},
  computed: {
    ...authComputed,
    availableBill() {
      if (this.bill && this.bill.id) {
        return this.bill
      } else {
        return this.billStub
      }
    },
    userIsAssigned() {
      if (
        this.trackedBill &&
        this.trackedBill.ID > 0 &&
        this.trackedBill.TrackedBillAssignments &&
        this.trackedBill.TrackedBillAssignments.length > 0
      ) {
        let assignment = this.trackedBill.TrackedBillAssignments.find(
          (i) => i.UserID == this.currentUser.ID
        )
        if (assignment && assignment.TrackedBillID == this.trackedBill.ID) {
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    },
    owningAgencies() {
      if (this.trackedBill && this.trackedBill.ID > 0) {
        return this.trackedBill.owningAgencies
      } else {
        return []
      }
    },
    userHasEditPerms() {
      return (
        this.trackedBill.currentUserPermissionID == 1 ||
        this.trackedBill.currentUserPermissionID == 2 ||
        this.trackedBill.currentUserPermissionID == 5
      )
    },
  },
  methods: {
    ...call('trackedBill', [
      'setSelectedTrackedBill',
      'refreshSelectedTrackedBill',
      'deleteTrackedBill',
    ]),
    loadTrackedBill() {
      this.$emit('refreshTrackedBills')
    },
    selectBill(bill) {
      this.setSelectedTrackedBill(bill)
      this.$router.push({
        name: 'bill',
        params: { id: bill.ID },
      })
    },
    amendmentLink(amendment) {
      var spSess = this.availableBill.specialSessionValue
        ? 'Sp' + this.availableBill.specialSessionValue
        : ''
      var amendmentExtension = this.availableBill.year < 2015 ? 'htm' : 'pdf'
      return `${this.basePdfLink}${this.availableBill.year}${spSess}/Amends/${amendment.amendmentNumber}.${amendmentExtension}`
    },
    refreshPermissions() {
      this.refreshSelectedTrackedBill()
    },
  },
  watch: {},
}
</script>
<style scoped>
.viewer {
  border-radius: 5px;
  /* margin: 5px; */
  max-height: 500px;
  min-height: 450px;
  height: auto;
  background-color: #f7f7f7;
  -webkit-box-shadow: inset 0 0 10px #e2e2e2;
  box-shadow: inset 0 0 10px #e2e2e2;
  overflow-y: scroll;
  padding: 10px;
}
</style>
